import { Choices } from '@customTypes/Choices'
import { ContactFormFields } from '@customTypes/ContactFormFields'
import { AdditionalAppointmentHints, BookingData, BookingV2 } from '@customTypes/Booking'
import { BookingFor } from '@customTypes/BookingFor'
import { Practitioner } from '@customTypes/Practitioner'
import { SourceWebsiteType } from '../lib'
import { format } from 'date-fns'
import { BIRTDATE_FORMAT_DATE_FNS } from '@screens/Contact/components/Birthdate/Birthdate'
import { parse } from 'date-fns/fp'
import { PatientTypeOptions } from 'src/config/patientTypeOptions'
import { ConfigState } from '@context/ConfigurationContext'

const prepareBooking = (
  choices: Choices,
  contactDetails: ContactFormFields,
  config: ConfigState,
  lang: string,
  practitioner: Practitioner | undefined,
  creationSource: SourceWebsiteType,
  creationEvent: string,
  treatmentBundleTypeReferenceId: string,
  phoneVerificationCode?: string,
  additionalAppointmentHints?: AdditionalAppointmentHints | undefined
): BookingV2 => {
  const { trackingParams, originalClinicId } = config
  const clinicId = choices.selectedBookableDateAndTime.clinicId || config.clinicId
  const originalClinicIdParam = originalClinicId !== clinicId ? originalClinicId : undefined

  const { selectedBookableDateAndTime, patientType, insuranceType, bookingFor, isManualReviewRequired } = choices
  const {
    firstName,
    lastName,
    email,
    consent: agbAccepted,
    phone: phoneNumber,
    gender,
    dentalTosAccepted,
    insurance_holder: insuranceHolder,
    patient_child,
    patient_firstName,
    patient_lastName,
    patient_gender,
    bookingFor: bookingForForm,
    reasonForVisit,
    birthdate,
    patient_birthdate,
  } = contactDetails

  const isBookingForAnotherPerson =
    bookingFor === BookingFor.BOOKING_FOR_OTHER || bookingForForm === BookingFor.BOOKING_FOR_OTHER

  const formBirthdate = isBookingForAnotherPerson ? patient_birthdate : birthdate
  const formattedBirthDate = format(
    parse(new Date())(BIRTDATE_FORMAT_DATE_FNS.UI)(formBirthdate),
    BIRTDATE_FORMAT_DATE_FNS.PAYLOAD
  )
  const isNewPatient = patientType === PatientTypeOptions.NEW

  const booking: Partial<BookingData> = {
    agbAccepted,
    dentalTosAccepted,
    ...(insuranceHolder && isBookingForAnotherPerson && { insuranceHolder }),
    contactPerson: isBookingForAnotherPerson
      ? {
          correspondence: {
            email,
            phoneNumber,
          },
          data: {
            firstName,
            lastName,
            gender: gender.toUpperCase(),
          },
        }
      : null,
    patient: isBookingForAnotherPerson
      ? {
          child: patient_child || false,
          ...(isNewPatient && { insuranceType }),
          data: {
            firstName: patient_firstName,
            lastName: patient_lastName,
            gender: patient_gender?.split('_')[1].toUpperCase(),
          },
          birthdate: formattedBirthDate,
        }
      : {
          data: {
            firstName,
            lastName,
            gender: gender.toUpperCase(),
          },
          correspondence: {
            email,
            phoneNumber,
          },
          ...(isNewPatient && { insuranceType }),
          birthdate: formattedBirthDate,
        },
    treatmentBundleTypeReferenceId,
    preferredPractitionerReferenceId: practitioner?.referenceId,
    start: selectedBookableDateAndTime.originalStartTimestamp,
    patientType,
    creationSource,
    creationEvent,
    phoneVerificationCode,
    additionalAppointmentHints,
  }

  if (isManualReviewRequired) {
    booking.reasonForVisit = reasonForVisit
  }

  return {
    booking,
    trackingParams: { ...trackingParams, originalClinicId: originalClinicIdParam },
    clinicId,
    lang,
  }
}

export default prepareBooking
